<script setup>
import {defineComponent, h, ref, watch} from "vue";
import {Switch, SwitchGroup, SwitchLabel} from '@headlessui/vue'
import PrimaryButton from "@/Jetstream/PrimaryButton.vue";
import AppLayout from "@/Layouts/AppLayout";
import { Inertia } from '@inertiajs/inertia'
import SecondaryButton from "@/Jetstream/SecondaryButton";
import DangerButton from "@/Jetstream/DangerButton";
import { useForm } from '@inertiajs/inertia-vue3'
import ConfirmationModal from "@/Jetstream/ConfirmationModal";

const props = defineProps({
  plans: {
    type: Array,
    default: () => [],
  },
  features: {
    type: Object,
    default: () => ({}),
  },
  limits: {
    type: Object,
    default: () => ({}),
  },
});

const icons = {
  checked: defineComponent({
    render() {
      return h('svg', {
        xmlns: 'http://www.w3.org/2000/svg',
        viewBox: '0 0 37.38 45.98'
      }, [
        h('path', {
          fill: 'currentColor',
          d: 'M23.54,0l-8,32.44L12.87,21.69H0L7.32,46H23.4l14-46Z'
        })
      ]);
    }
  }),
}

const selectedPeriodAnnually = ref(false);
const confirmingPurchase = ref(false);
const selectedPlan = ref(null);

const selectPlan = (plan) => {
  selectedPlan.value = plan;
  confirmingPurchase.value = true;
};

const purchasePlan = () => {
  form.post(route('billing.chose-plan', {package: selectedPlan.value}));
}

const form = useForm();
</script>

<template>
  <app-layout title="Licenses">

    <template #header>
      <div class="flex items-center justify-between">
        <h1 class="text-3xl font-bold tracking-tight text-white">
          {{ __('New License') }}
        </h1>
        <SwitchGroup as="div" class="flex items-center">
          <Switch v-model="selectedPeriodAnnually"
                  :class="[selectedPeriodAnnually ? 'bg-primary' : 'bg-gray-200', 'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2']">
                <span aria-hidden="true"
                      :class="[selectedPeriodAnnually ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out']"/>
          </Switch>
          <SwitchLabel as="span" class="ml-3 flex space-x-2">
            <span class="text-sm font-medium text-gray-300">{{ __('Annual billing') }}</span>
            <span class="text-sm text-gray-400">{{ __('(Save 20%)') }}</span>
          </SwitchLabel>
        </SwitchGroup>
      </div>
    </template>

    <div
        class="mt-6 space-y-4 sm:mt-8 sm:grid sm:grid-cols-2 sm:gap-6 sm:space-y-0 lg:mx-auto lg:max-w-4xl xl:mx-0 xl:max-w-none xl:grid-cols-4 transition-all ease-in-out duration-300">
      <div
          v-for="plan in plans.filter(plan => selectedPeriodAnnually ? plan.billing_cycle == '365 days' : plan.billing_cycle == '30 days').sort((a, b) => a.price - b.price)"
          :key="plan.id"
          class="divide-y divide-gray-200 rounded-lg shadow bg-white border-gray-200"
          :class="false ? 'relative before:absolute before:top-0 before:left-0 before:w-1/2 before:h-1 before:rounded-bl-xl before:rounded-br-xl before:bg-primary before:-translate-x-1/2 before:left-1/2' : ''"
      >
        <div class="p-6">
          <h2 class="text-lg font-medium leading-6 text-gray-900">{{ plan.name }}</h2>
<!--              <p class="mt-4 text-sm text-gray-500">{{ plan.description }}</p>-->
          <p class="mt-8">
          <span class="text-4xl font-bold tracking-tight text-gray-900">{{
              price(plan.price)
            }}</span>
            {{ ' ' }}
            <span class="text-base font-medium text-gray-500">/{{
                __(!selectedPeriodAnnually ? 'monthly' : 'annually')
              }}</span>
          </p>
          <primary-button :modern="false" @click="selectPlan(plan)"
                          class="block w-full h-10 mt-8 text-center inline-flex items-center justify-center bg-primary text-white text-medium font-semibold hover:bg-opacity-75 hover:bg-primary-500">
              <span v-if="selectedPeriodAnnually">{{ __('Purchase and safe 20% off') }}</span>
              <span v-else>{{ __('Purchase') }}</span>
          </primary-button>
        </div>
        <div class="px-6 pt-6 pb-8">
          <h3 class="text-sm font-medium text-gray-900">{{ __('What\'s included') }}</h3>
          <ul role="list" class="mt-6 space-y-4">
            <!-- Tags -->
            <li v-for="feature in plan.tags.sort((a, b) => a.sort_id - b.sort_id)" :key="feature.sort_id" class="flex space-x-3">
              <component :is="icons.checked"
                         class="h-5 w-5 flex-shrink-0 text-primary focus:outline-none"
                         aria-hidden="true"/>
              <span class="text-sm text-gray-500">{{ feature.tag }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <ConfirmationModal :show="confirmingPurchase" @close="confirmingPurchase = false">
      <template #title>
        {{ __('Create new license') }}
      </template>

      <template #content>
        {{ __('Are you sure you want to create a new license? By clicking Purchase a new license will be created for you and the amount will be deducted from your payment method.') }}
      </template>

      <template #footer>
        <SecondaryButton @click.native="confirmingPurchase = false">
          {{ __('Nevermind') }}
        </SecondaryButton>

        <DangerButton :modern="false" class="ml-2" @click.native="purchasePlan" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
          {{ __('Continue') }}
        </DangerButton>
      </template>
    </ConfirmationModal>
  </app-layout>
</template>